import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TreeView from '@mui/lab/TreeView';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {DeleteForever, Description, DescriptionOutlined, Folder, FolderOutlined, Timeline} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { MenuItem, Menu } from "@mui/material";
import { listFiles } from "../API/KB/Files";
import { convertHexToRgba, formatBytes, isMobile, timeAgo } from "../App/utils";
import { getFilePathByKey, isNewFile } from "./CodeEditorUtils";
import { colorText } from "../App/theme";
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from "@mui/material/Divider";
import {useConfirm} from "../Components/ConfirmContext";
import {insecureSecretWarning} from "./SecretsList";

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
    color: theme.palette.text.secondary,
    [`& .${treeItemClasses.content}`]: {
        marginLeft: 0,
        color: theme.palette.text.secondary,
        borderTopRightRadius: theme.spacing(2),
        borderBottomRightRadius: theme.spacing(2),
        paddingRight: theme.spacing(1),
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: '0.875rem',
        '&.Mui-expanded': {
            fontWeight: theme.typography.fontWeightRegular,
        },
        '&:hover:not(.Mui-selected)': {
            backgroundColor: 'transparent !important',
        },
        '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
            backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
        },
        [`& .${treeItemClasses.label}`]: {
            fontWeight: 'inherit',
            color: 'inherit',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
        },
    },
    [`& .${treeItemClasses.group}`]: {
        marginLeft: theme.spacing(2),
        [`& .${treeItemClasses.content}`]: {
            paddingLeft: theme.spacing(2),
        },
    },
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
    fontSize: '0.875rem',
    '& .MuiSvgIcon-root': {
        fontSize: '1rem',
        marginRight: theme.spacing(1),
    },
}));

const InfoTypography = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    margin: theme.spacing(2),
}));

function getIconProps(type, inactive, color, size = 18, margin = 0) {
    return {
        width: size,
        src: `/static/icons/${type}${inactive}.svg`,
        alt: type.toUpperCase(),
        style: { marginRight: (8 + margin * 2), color, marginLeft: margin }
    };
}

export function renderItem(item) {
    return item?.folder ? renderFolder(item) : renderFile(item);
}

function renderFile(item, color = null) {
    const rootFolderName = item?.Key?.split('/')?.[2];
    const name = item?.Key?.split('/')?.pop();
    const inactive = color ? '-inactive' : '';
    let iconType = '';
    let size = 16;
    let margin;

    if (name?.endsWith('.json')) {
        return <img {...getIconProps('json', inactive, color, size, margin)} />;
    }

    if (rootFolderName === 'Events' && name?.endsWith('.js')) {
        iconType = 'nodejs';
    } else if (rootFolderName === 'Frontend' && name?.endsWith('.js')) {
        iconType = 'react';
        size = 18;
    }

    if (iconType) {
        return <img {...getIconProps(iconType, inactive, color, size, margin)} />;
    }

    return <Description style={{ marginRight: 8, color, fontSize: '1.25rem' }} />;
}

function renderFolder(item, color = null) {
    const folderName = item?.folder;
    let iconType = '';
    let size = 18;
    let margin;

    if (folderName === 'Frontend') {
        size = 21;
        margin = -1
        iconType = 'react';
    } else if (folderName === 'Events') {
        iconType = 'nodejs-full';
    }

    if (iconType) {
        return <img {...getIconProps(iconType, '', color, size, margin)} />;
    }

    return <Folder style={{ marginRight: 8, fontSize: '1.25rem', color }} />;
}

function StyledTreeItem(props) {
    const theme = useTheme();
    const {
        bgColor,
        color,
        colorForDarkMode,
        bgColorForDarkMode,
        selectedNode,
        isNew,
        filename,
        Size,
        empty,
        LastModified,
        folder,
        onFileDelete,
        onFileCreate,
        item,
        setFilepathVersionsOpen,
        onFileOpen,
        toggleNode, // Function to toggle expansion
        setSelectedNode, // Function to set selected node
        ...other
    } = props;
    const [isMenuOpened, setMenuOpened] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const selected = props?.nodeId === props?.selectedNode?.nodeId;
    const styleProps = {
        '--tree-view-color': theme.palette.mode !== 'dark' ? color : colorForDarkMode,
        '--tree-view-bg-color': theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
    };
    const itemColor = (filename && empty || folder === 'dist') && convertHexToRgba(colorText, 0.3);
    let folderName = folder;
    // if (folder === 'Events') folderName = 'Events';
    // if (folder === 'Frontend') folderName = 'Frontend';

    const handleLabelClick = (event) => {
        event.stopPropagation();
        setSelectedNode({ nodeId: props.nodeId, item });
        // If single click on mobile, open the file
        if (isMobile) {
            if (folder) {
                // Toggle expansion for folders
                toggleNode(props.nodeId);
            } else if (filename) {
                // Open file on double-click for files
                onFileOpen(item);
            }
        }
    };

    const handleLabelDoubleClick = (event) => {
        event.stopPropagation();
        if (folder) {
            // Toggle expansion for folders
            toggleNode(props.nodeId);
        } else if (filename) {
            // Open file on double-click for files
            onFileOpen(item);
        }
    };

    return (
        <StyledTreeItemRoot
            label={
                <Box
                    onTouchEnd={(event) => {
                        event.stopPropagation();
                    }}
                    onContextMenu={(event) => {
                        event.preventDefault();
                        setAnchorEl(event.currentTarget);
                        setMenuOpened(!isMenuOpened);
                    }}
                    onClick={handleLabelClick}
                    onDoubleClick={handleLabelDoubleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: folder ? '1px' : '1px',
                        marginBottom: folder ? '1px' : '1px',
                        p: 0.1,
                        pb: 0.2,
                        pr: 0,
                        userSelect: 'none', // Prevent text selection on double click
                        cursor: 'pointer',
                    }}
                >
                    <Box
                        component={filename ? () => renderFile(item, itemColor) : () => renderFolder(item, itemColor)}
                        color="inherit"
                        sx={{ mr: 1 }}
                    />
                    <Typography component="div" variant="body2" sx={{
                        fontWeight: 'inherit',
                        flexGrow: 1,
                        color: itemColor
                    }}>
                        {filename || folderName}
                    </Typography>
                    <ClickAwayListener
                        onClickAway={(event) => {
                            event.stopPropagation();
                            setMenuOpened(false);
                            setAnchorEl(null);
                        }}
                    >
                        <Menu
                            open={isMenuOpened}
                            style={{ zIndex: 9999 }}
                            anchorEl={anchorEl}
                            onClose={(event) => {
                                event.stopPropagation();
                                setMenuOpened(false);
                                setAnchorEl(null);
                            }}
                        >
                            {(empty && !item?.folder) ? (
                                <CustomMenuItem
                                    onClick={(e) => {
                                        onFileOpen(item);
                                        setMenuOpened(false);
                                        e.stopPropagation();
                                    }}
                                >
                                    Click to create
                                </CustomMenuItem>
                            ) : (
                                <div>
                                    {item?.folder && (
                                        <CustomMenuItem
                                            onClick={(e) => {
                                                onFileCreate(item);
                                                setMenuOpened(false);
                                                e.stopPropagation();
                                            }}
                                        >
                                            <DescriptionOutlined />
                                            New File
                                        </CustomMenuItem>
                                    )}
                                    {item?.folder && (
                                        <CustomMenuItem
                                            onClick={(e) => {
                                                onFileCreate({...item, isFolder: true});
                                                setMenuOpened(false);
                                                e.stopPropagation();
                                            }}
                                        >
                                            <FolderOutlined />
                                            New Folder
                                        </CustomMenuItem>
                                    )}
                                    {item?.Key && (
                                        <CustomMenuItem
                                            onClick={() => {
                                                setMenuOpened(false);
                                                setFilepathVersionsOpen(getFilePathByKey(item.Key));
                                            }}
                                        >
                                            <Timeline />
                                            Show History
                                        </CustomMenuItem>
                                    )}
                                    <CustomMenuItem
                                        onClick={(e) => {
                                            onFileDelete();
                                            setMenuOpened(false);
                                            e.stopPropagation();
                                        }}
                                    >
                                        <DeleteForever />
                                        Delete{item?.folder && ` files in "${item.folder}"`}
                                    </CustomMenuItem>
                                    <Divider><InfoTypography style={{margin: 0, padding: 0}} >Info</InfoTypography></ Divider>
                                    <InfoTypography style={{marginTop: 2, marginBottom: 2}}>{item?.folder ? 'Folder' : 'File'}: {(item?.Key || item?.folder)?.split('/')?.splice(-1)}</InfoTypography>
                                    {!!LastModified && <InfoTypography style={{marginTop: 2, marginBottom: 2}}>Size: {formatBytes(Size)}</InfoTypography>}
                                    {!!LastModified && <InfoTypography style={{marginTop: 2, marginBottom: 2}}>Modified: {timeAgo(LastModified)}</InfoTypography>}
                                </div>
                            )}
                        </Menu>
                    </ClickAwayListener>
                </Box>
            }
            style={styleProps}
            selected={selected} // Ensure TreeItem highlights as selected
            {...other}
        />
    );
}

export function FilesEditor({ isFullscreen, onFileOpen, onFileCreate, onFileDelete, setFilesData, filesData, setFilepathVersionsOpen }) {
    const [selectedNode, setSelectedNode] = useState(null);
    const [fileItemCreateLastSeconds, setFileItemCreateLastSeconds] = useState(null);
    const [expanded, setExpanded] = useState(['1','2','3','4']);

    const showConfirm = useConfirm();

    useEffect(() => {
        const itemCreateLastSeconds = filesData?.flatMap(item => item?.children || [])
            .sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified))
            .find(child => isNewFile(child));
        setFileItemCreateLastSeconds(itemCreateLastSeconds || null)
    }, [filesData]);

    useEffect(() => {
        listFiles('frontend').then((data) => {
            setFilesData((prevData) => [...prevData.filter((o) => o.folder !== 'Frontend'), ...data]);
        });
        listFiles('functions').then((data) => {
            setFilesData((prevData) => [...prevData.filter((o) => o.folder === 'Frontend'), ...data]);
        });
    }, [setFilesData]);

    const toggleNode = (nodeId) => {
        setExpanded((prevExpanded) => {
            if (prevExpanded.includes(nodeId)) {
                return prevExpanded.filter(id => id !== nodeId);
            } else {
                return [...prevExpanded, nodeId];
            }
        });
    };

    const handleNodeToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const renderTree = (items, startNodeId = 0) => {
        let nodeId = startNodeId;
        items.sort((a, b) => {
            const isAFolder = a.hasOwnProperty('folder');
            const isBFolder = b.hasOwnProperty('folder');
            if (isAFolder && !isBFolder) return -1;
            if (!isAFolder && isBFolder) return 1;
            const nameA = (a?.folder || a?.filename)?.toUpperCase();
            const nameB = (b?.folder || b?.filename)?.toUpperCase();
            const baseNameA = nameA.split('.')[0];
            const baseNameB = nameB.split('.')[0];
            const extensionA = nameA.split('.').pop();
            const extensionB = nameB.split('.').pop();
            if (baseNameA === baseNameB) {
                if (extensionA === 'JS' && extensionB === 'JSON') return -1;
                if (extensionA === 'JSON' && extensionB === 'JS') return 1;
            }
            if (nameA > nameB) return -1;
            if (nameA < nameB) return 1;
            return 0;
        });

        const jsFilesStatus = new Map(
            items.filter((item) => item.filename && item.filename.endsWith('.js')).map((item) => [item.filename.replace('.js', ''), item.empty])
        );

        return items.map((item) => {
            nodeId++;
            const currentId = nodeId.toString();
            if (item.empty && item.filename && item.filename.endsWith('.json')) {
                const baseName = item.filename.replace('.json', '');
                if (jsFilesStatus.has(baseName) && jsFilesStatus.get(baseName)) return null;
            }
            const isNew = fileItemCreateLastSeconds?.Key && fileItemCreateLastSeconds?.Key === item?.Key && isNewFile(fileItemCreateLastSeconds);
            return (
                <Box key={currentId} sx={{ textAlign: 'left' }}>
                    <StyledTreeItem
                        className={isNew ? 'new-file-created' : ''}
                        onFileDelete={async () => {
                            const itemToDelete = item?.folder ? (`all files in ${item.folder}`) : getFilePathByKey(item.Key);

                            const confirmed = await showConfirm({
                                title: 'Delete Confirmation',
                                content: `Are you sure you want to delete "${itemToDelete}"?`,
                            });

                            if (!confirmed) return;
                            (item?.children ?? [item])
                                .forEach(({ filename, Key }) => (filename || filename === '') && Key && onFileDelete(getFilePathByKey(Key)))
                        }}
                        onFileCreate={onFileCreate}
                        item={item}
                        setFilepathVersionsOpen={setFilepathVersionsOpen}
                        empty={item?.empty}
                        Size={item?.Size}
                        LastModified={item?.LastModified}
                        filename={item?.filename}
                        folder={item?.folder}
                        selectedNode={selectedNode}
                        isNew={isNew}
                        nodeId={currentId}
                        color={item.color}
                        bgColor={item.bgColor}
                        colorForDarkMode={item.colorForDarkMode}
                        bgColorForDarkMode={item.bgColorForDarkMode}
                        onFileOpen={onFileOpen}
                        toggleNode={toggleNode} // Pass toggleNode to StyledTreeItem
                        setSelectedNode={setSelectedNode} // Pass setSelectedNode to StyledTreeItem
                    >
                        {item.children && item.children?.find(o => o.filename || o.folder)
                            // filter out empty file in folder
                            && renderTree(item.children?.filter(o => o.filename !== ''), nodeId * 1000)}
                    </StyledTreeItem>
                </Box>
            );
        });
    };

    return (
        <TreeView
            aria-label="functions manager"
            expanded={expanded}
            onNodeToggle={handleNodeToggle}
            defaultCollapseIcon={<ArrowDropDownIcon />}
            defaultExpandIcon={<ArrowRightIcon />}
            defaultEndIcon={<div style={{ width: 24 }} />}
            selected={selectedNode ? selectedNode.nodeId : null} // Ensure only one node is selected
            sx={{ height: '100%', flexGrow: 1, marginLeft: isFullscreen ? '0px' : '-4px', overflowY: 'auto' }}
        >
            {renderTree(filesData)}
        </TreeView>
    );
}